import type { CSSProperties, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const inp: CSSProperties = { padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--text)", fontSize: 14, width: "100%" };

/** Formata "2026-08-08" (ISO cru do backend) como "08/08/2026". Retorna
 *  "—" pra vazio/null. Auditoria UX 27/07/2026 tier 4: varias tabelas
 *  da prefeitura mostravam ISO cru no lugar de pt-BR. */
export function fmtDataISO(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Download a protected endpoint attaching the stored JWT (a plain <a> can't send headers). */
/**
 * Le o nome do arquivo do `content-disposition`, se houver.
 *
 * O SERVIDOR SABE O NOME, A TELA NAO. Desde que o movimento pode sair num
 * layout adaptado, o nome deixou de ser `movimento_AAAAMM.csv` — o adaptador do
 * ente escolhe o dele (`ALECE-MOVIMENTO-2609.TXT`, por exemplo). Um literal na
 * tela entregaria ao operador um arquivo com nome que o sistema dele nao
 * reconhece.
 */
function nomeDoContentDisposition(cd: string | null): string | null {
  if (!cd) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (utf8?.[1]) return decodeURIComponent(utf8[1].trim());
  const simples = /filename="?([^";]+)"?/i.exec(cd);
  return simples?.[1]?.trim() || null;
}

/** `filename` e' o FALLBACK: o nome do `content-disposition` tem precedencia. */
export async function downloadAuthed(url: string, filename: string): Promise<void> {
  const raw = typeof window !== "undefined" ? window.localStorage.getItem("atlas:tokens") : null;
  const token = raw ? (JSON.parse(raw) as { access_token?: string }).access_token : null;
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    // A recusa que diz o que corrigir (movimento com FF ou DESCRUB vazio) vem
    // em `error.message`; so' o status deixava o operador sem saber o que fazer.
    const corpo = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    alert(corpo?.error?.message ? `Erro ao baixar: ${corpo.error.message}` : `Erro ao baixar (HTTP ${res.status}).`);
    return;
  }
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = nomeDoContentDisposition(res.headers.get("content-disposition")) ?? filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}
export const selStyle: CSSProperties = { ...inp, cursor: "pointer" };

/**
 * Uma célula estreita, cortada com reticências. O valor inteiro fica no `title`.
 *
 * A largura vai AQUI, no conteúdo, e não na coluna: a `DataTable` aplica
 * `width` só no `<th>`, e a tabela é `table-layout: auto` — nesse modo a
 * largura declarada é apenas uma sugestão, que o navegador ignora assim que uma
 * célula `nowrap` for maior. Um bloco com `maxWidth` e `overflow: hidden`
 * não tem essa saída: ele corta, e a coluna para de crescer junto.
 */
export function Corta({ children, titulo, largura, estilo }: {
  children: ReactNode;
  titulo?: string;
  largura: number;
  estilo?: CSSProperties;
}) {
  return (
    <span
      title={titulo}
      style={{
        display: "block",
        maxWidth: largura,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...estilo,
      }}
    >
      {children}
    </span>
  );
}

/**
 * Botao de "← Voltar" contextual. Usa nav(-1) quando o usuario chegou aqui
 * navegando por dentro do app (React Router preenche uma chave nao-default
 * na location). Se caiu direto (recarregou, colou o link, veio de fora),
 * vai pro caminho de fallback informado.
 */
export function BackLink({ fallback, label = "← Voltar" }: { fallback: string; label?: string }) {
  const nav = useNavigate();
  const location = useLocation();
  const hasHistory = location.key !== "default";
  const onClick = () => {
    if (hasHistory) nav(-1);
    else nav(fallback);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        color: "var(--text-muted)",
        fontSize: 13,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginBottom: 4,
      }}
    >
      {label}
    </button>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
      <div>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>Prefeitura</span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>{title}</h1>
        {subtitle ? <p style={{ color: "var(--text-muted)", marginTop: 4 }}>{subtitle}</p> : null}
      </div>
      {actions ? <div style={{ display: "flex", gap: 8, alignItems: "center" }}>{actions}</div> : null}
    </header>
  );
}

export function Modal({ title, onClose, children, maxWidth = 560 }: { title: string; onClose: () => void; children: ReactNode; maxWidth?: number }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center", zIndex: 100, padding: 24 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "var(--surface-solid, var(--surface))", borderRadius: 12, padding: 24, maxWidth, width: "100%", border: "1px solid var(--border)", maxHeight: "90vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

/**
 * Rotulo + campo.
 *
 * `para` (feature 008, FR-039) e' o `htmlFor`: o rotulo envolve o campo, o que
 * ja' o nomeia, mas o contrato pede a ligacao EXPLICITA — ela e' a unica que
 * sobrevive a um campo que saia de dentro do rotulo, e e' o que o teste de
 * navegador procura. Opcional para nao mexer nas dezenas de chamadas que ja'
 * existem; quem passa `para` tem de por o mesmo `id` no campo filho.
 */
export function Field({ lbl, children, hint, required, para }: { lbl: string; children: ReactNode; hint?: string; required?: boolean; para?: string }) {
  return (
    <label htmlFor={para} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
        {lbl}
        {required ? <span aria-label="obrigatório" style={{ color: "var(--danger-500)", marginLeft: 4 }}>*</span> : null}
      </span>
      {children}
      {hint ? <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{hint}</span> : null}
    </label>
  );
}
